import React, { useState, useEffect, useRef } from 'react';
import '../../../Styles/Renderer/imageDisplay.css';
import { useAttachmentContext } from '../../../contexts/AttachmentContext';

interface ImageDisplayProps {
  src: string;
  alt?: string;
  title?: string;
}

export const ImageDisplay: React.FC<ImageDisplayProps> = ({ src, alt, title }) => {
  const { handleImageClick } = useAttachmentContext();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [hasError, setHasError] = useState(false);
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    // Check if image is already cached
    const img = new Image();
    img.src = src;
    
    if (img.complete && img.naturalWidth > 0) {
      // Image is already cached, show it immediately
      setShowLoadingSpinner(false);
    } else {
      // Image needs to load, set initial loading state
      // Only show spinner after 300ms delay
      loadingTimerRef.current = setTimeout(() => {
        setShowLoadingSpinner(true);
      }, 300);
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [src]);

  const handleLoad = () => {
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
    }
    setShowLoadingSpinner(false);
    setHasError(false);
  };

  const handleError = () => {
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
    }
    setShowLoadingSpinner(false);
    setHasError(true);
  };

  const handleClick = () => {
    if (!hasError) {
      handleImageClick(src, alt || title || 'Image');
    }
  };

  if (hasError) {
    return (
      <div className="image-display-error">
        <div className="image-error-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
              fill="currentColor"
              opacity="0.3"
            />
            <path
              d="M10 10.5C10.8284 10.5 11.5 9.82843 11.5 9C11.5 8.17157 10.8284 7.5 10 7.5C9.17157 7.5 8.5 8.17157 8.5 9C8.5 9.82843 9.17157 10.5 10 10.5Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <p className="image-error-text">Failed to load image</p>
        {src && (
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="image-error-link"
          >
            View original
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="image-display-container">
      {showLoadingSpinner && (
        <div className="image-display-loading">
          <div className="image-loading-spinner" />
          <p className="image-loading-text">Loading image...</p>
        </div>
      )}
      <img
        ref={imageRef}
        src={src}
        alt={alt || 'Image'}
        title={title}
        className={`image-display ${showLoadingSpinner ? 'hidden' : ''}`}
        onLoad={handleLoad}
        onError={handleError}
        onClick={handleClick}
        decoding="async"
        fetchPriority="high"
      />
      {!showLoadingSpinner && !hasError && (
        <div className="image-display-overlay" onClick={handleClick}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="image-zoom-icon"
          >
            <path
              d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
              fill="currentColor"
            />
            <path
              d="M12 10H10V12H9V10H7V9H9V7H10V9H12V10Z"
              fill="currentColor"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

